import {
  Create,
  DateField,
  Edit,
  Filter,
  List,
  NumberField,
  NumberInput,
  Pagination,
  ReferenceField,
  ReferenceInput,
  Resource,
  SelectInput,
  Show,
  SimpleForm,
  TextField,
  TextInput,
  useListContext
} from "react-admin";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  makeStyles
} from "@material-ui/core";
import { Link, useHistory } from "react-router-dom";
import {
  BcomeVersionFilterInput,
  useBcomeVersionMap,
} from "../components/DbVersion";

// Neutral colors for all groups (borders provide separation)
const GROUP_COLORS = [
  { header: '', subHeader: '', cell: '#fff' },
  { header: '', subHeader: '', cell: '#fff' },
  { header: '', subHeader: '', cell: '#fff' },
  { header: '', subHeader: '', cell: '#fff' },
  { header: '', subHeader: '', cell: '#fff' },
];

// Styles for the grouped table
const useStyles = makeStyles({
  tableContainer: {
    marginTop: '15px',
    overflowX: 'auto',
  },
  table: {
    minWidth: 3500,
    borderCollapse: 'collapse',
  },
  headerGroup: {
    fontWeight: 'bold',
    textAlign: 'center',
    borderBottom: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    padding: '10px 8px',
    fontSize: '0.85rem',
  },
  headerSubGroup: {
    fontWeight: 600,
    textAlign: 'center',
    fontSize: '0.7rem',
    padding: '6px 2px',
    borderBottom: '1px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  headerSubGroupFirst: {
    borderLeft: '1px solid #e0e0e0',
  },
  headerSubGroupLast: {
    borderRight: '1px solid #e0e0e0',
  },
  headerFixed: {
    backgroundColor: '',
    fontWeight: 'bold',
    textAlign: 'center',
    borderBottom: '1px solid #e0e0e0',
    padding: '10px 8px',
  },
  cell: {
    textAlign: 'right',
    padding: '6px 4px',
    fontSize: '0.75rem',
    borderBottom: '1px solid #e0e0e0',
  },
  cellFirst: {
    borderLeft: '1px solid #e0e0e0',
  },
  cellLast: {
    borderRight: '1px solid #e0e0e0',
  },
  cellLeft: {
    textAlign: 'left',
    padding: '6px 8px',
    fontSize: '0.8rem',
    backgroundColor: '#fff',
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover td': {
      backgroundColor: '#f0f0f0 !important',
    },
  },
});

// Indicator definitions — same order as Article LCA, WC appended at end
const INDICATORS = [
  { key: 'WS',     label: 'Water Scarcity',                unit: 'm³ water eq'   },
  { key: 'GW',     label: 'Climate Change',                unit: 'kg CO₂ eq'     },
  { key: 'Eutr',   label: 'Freshwater eutrophication',     unit: 'g P eq'        },
  { key: 'AD',     label: 'Fossil Fuels',                  unit: 'MJ'            },
  { key: 'OD',     label: 'Ozone Depletion',               unit: 'kg CFC-11 eq'  },
  { key: 'HTC',    label: 'Human Toxicity Cancer',         unit: 'CTUh'          },
  { key: 'HTnC',   label: 'Human Toxicity Non-Cancer',     unit: 'CTUh'          },
  { key: 'PM',     label: 'Particulate Matter',            unit: 'disease inc.'  },
  { key: 'IR',     label: 'Ionizing Radiation',            unit: 'kBq U-235 eq'  },
  { key: 'POF',    label: 'Photochem. Ozone Formation',    unit: 'kg NMVOC eq'   },
  { key: 'Acid',   label: 'Acidification',                 unit: 'mol H⁺ eq'     },
  { key: 'TEutr',  label: 'Terrestrial Eutrophication',    unit: 'mol N eq'      },
  { key: 'MEutr',  label: 'Marine Eutrophication',         unit: 'kg N eq'       },
  { key: 'Ecotox', label: 'Freshwater Ecotoxicity',        unit: 'CTUe'          },
  { key: 'LU',     label: 'Land Use',                      unit: 'Pt'            },
  { key: 'ADMM',   label: 'Resource Use Min. & Metals',    unit: 'kg Sb eq'      },
  { key: 'WC',     label: 'Water Consumption',             unit: 'liters water'  },
];

// Sub-columns for each indicator
const SUB_COLUMNS = [
  { suffix: 'RME_U_Mat',      label: 'Extraction'    },
  { suffix: 'MP_U_Mat',       label: 'Processing'    },
  { suffix: 'MF_U_Mat',       label: 'Finishing'     },
  { suffix: 'total_U_Mat',    label: 'Total'         },
  { suffix: 'RME_U_Mat_IS',   label: 'Extraction IS' },
  { suffix: 'MP_U_Mat_IS',    label: 'Processing IS' },
  { suffix: 'MF_U_Mat_IS',    label: 'Finishing IS'  },
  { suffix: 'total_U_Mat_IS', label: 'Total IS'      },
];

const STAGES = [
  { suffix: 'RME',   label: 'Extraction' },
  { suffix: 'MP',    label: 'Processing' },
  { suffix: 'MF',    label: 'Finishing'  },
  { suffix: 'total', label: 'Total'      },
];

const matField   = (key, stage) => `${key}_${stage}_U_Mat`;
const matFieldIS = (key, stage) => `${key}_${stage}_U_Mat_IS`;

// Format number for display
const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value !== 'number') return value;

  if (Math.abs(value) < 0.001 && value !== 0) {
    return value.toExponential(3);
  }
  if (Math.abs(value) >= 1000000) {
    return value.toExponential(3);
  }

  return value.toLocaleString('en-US', {
    maximumFractionDigits: 6,
    minimumFractionDigits: 0
  });
};

// Custom Datagrid with grouped headers
const GroupedDatagrid = () => {
  const classes = useStyles();
  const { data, ids, loaded, basePath } = useListContext();
  const history = useHistory();
  const bcomeVersionMap = useBcomeVersionMap();

  if (!loaded) return <div>Loading...</div>;
  if (!ids || ids.length === 0) return <div>No results found</div>;

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className={classes.table} size="small">
        <TableHead>
          {/* First header row - Main groups */}
          <TableRow>
            <TableCell
              className={classes.headerFixed}
              rowSpan={2}
              style={{ minWidth: 80 }}
            >
              ID
            </TableCell>
            <TableCell
              className={classes.headerFixed}
              rowSpan={2}
              style={{ minWidth: 200 }}
            >
              Material
            </TableCell>
            <TableCell
              className={classes.headerFixed}
              rowSpan={2}
              style={{ minWidth: 150 }}
            >
              Project
            </TableCell>
            <TableCell
              className={classes.headerFixed}
              rowSpan={2}
              style={{ minWidth: 140 }}
            >
              Version Set
            </TableCell>

            {/* Indicator group headers */}
            {INDICATORS.map((indicator, groupIndex) => (
              <TableCell
                key={indicator.key}
                className={classes.headerGroup}
                colSpan={8}
                style={{
                  minWidth: 600,
                  backgroundColor: GROUP_COLORS[groupIndex % GROUP_COLORS.length].header,
                }}
              >
                {indicator.label} ({indicator.unit})
              </TableCell>
            ))}

            <TableCell
              className={classes.headerFixed}
              rowSpan={2}
              style={{ minWidth: 100 }}
            >
              Created
            </TableCell>
            <TableCell
              className={classes.headerFixed}
              rowSpan={2}
              style={{ minWidth: 100 }}
            >
              Updated
            </TableCell>
            <TableCell
              className={classes.headerFixed}
              rowSpan={2}
              style={{ minWidth: 80 }}
            >
              Actions
            </TableCell>
          </TableRow>

          {/* Second header row - Sub-columns */}
          <TableRow>
            {INDICATORS.map((indicator, groupIndex) => (
              SUB_COLUMNS.map((subCol, colIndex) => (
                <TableCell
                  key={`${indicator.key}_${subCol.suffix}`}
                  className={`${classes.headerSubGroup} ${colIndex === 0 ? classes.headerSubGroupFirst : ''} ${colIndex === 7 ? classes.headerSubGroupLast : ''}`}
                  style={{
                    backgroundColor: GROUP_COLORS[groupIndex % GROUP_COLORS.length].subHeader,
                  }}
                >
                  {subCol.label}
                </TableCell>
              ))
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {ids.map((id, rowIndex) => {
            const record = data[id];
            if (!record) return null;

            return (
              <TableRow
                key={id}
                className={classes.clickableRow}
                onClick={() => history.push(`${basePath}/${id}/show`)}
              >
                <TableCell className={classes.cellLeft}>
                  {record.id?.substring(0, 8)}...
                </TableCell>
                <TableCell className={classes.cellLeft}>
                  {record.material?.name || '-'}
                </TableCell>
                <TableCell className={classes.cellLeft}>
                  {record.project?.name || '-'}
                </TableCell>
                <TableCell className={classes.cellLeft}>
                  {bcomeVersionMap[record.project?.bcomeVersionId] || '-'}
                </TableCell>

                {/* Indicator values */}
                {INDICATORS.map((indicator, groupIndex) => (
                  SUB_COLUMNS.map((subCol, colIndex) => {
                    const fieldName = `${indicator.key}_${subCol.suffix}`;
                    return (
                      <TableCell
                        key={fieldName}
                        className={`${classes.cell} ${colIndex === 0 ? classes.cellFirst : ''} ${colIndex === 7 ? classes.cellLast : ''}`}
                        style={{
                          backgroundColor: rowIndex % 2 === 0
                            ? GROUP_COLORS[groupIndex % GROUP_COLORS.length].cell
                            : GROUP_COLORS[groupIndex % GROUP_COLORS.length].subHeader,
                        }}
                      >
                        {formatNumber(record[fieldName])}
                      </TableCell>
                    );
                  })
                ))}

                <TableCell className={classes.cellLeft}>
                  {record.createdAt ? new Date(record.createdAt).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell className={classes.cellLeft}>
                  {record.updatedAt ? new Date(record.updatedAt).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell className={classes.cellLeft}>
                  <Link to={`${basePath}/${id}`} onClick={(e) => e.stopPropagation()}>
                    Edit
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Filters for Material Impact
const Filters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="material.name" alwaysOn />
    <BcomeVersionFilterInput alwaysOn />
    <ReferenceInput label="Account" source="accountId" reference="accounts">
      <SelectInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Material" source="materialId" reference="material">
      <SelectInput optionText="name" />
    </ReferenceInput>
    <ReferenceInput label="Project" source="projectId" reference="projects">
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

// Custom pagination
const MaterialImpactPagination = props => (
  <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />
);

// List view with grouped headers
const MaterialImpactList = (props) => {
  return (
    <List
      {...props}
      filters={Filters(props)}
      bulkActionButtons={false}
      pagination={<MaterialImpactPagination />}
    >
      <GroupedDatagrid />
    </List>
  );
};

// Show view
const ShowMaterialImpact = (props) => {
  return (
    <Show {...props}>
      <SimpleForm>
        <TextField source="id" label="ID" />
        <ReferenceField label="Account" source="accountId" reference="accounts">
          <TextField source="name" />
        </ReferenceField>
        {/* GET /materialImpact/:id embeds material { id, name } and project { id, name } */}
        <TextField label="Material" source="material.name" />
        <TextField label="Project" source="project.name" />

        {INDICATORS.map(ind =>
          STAGES.map(st => [
            <NumberField key={matField(ind.key, st.suffix)}   source={matField(ind.key, st.suffix)}   label={`${ind.label} - ${st.label} (${ind.unit})`}    emptyText="-" />,
            <NumberField key={matFieldIS(ind.key, st.suffix)} source={matFieldIS(ind.key, st.suffix)} label={`${ind.label} - ${st.label} IS (${ind.unit})`}  emptyText="-" />,
          ])
        )}

        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
      </SimpleForm>
    </Show>
  );
};

// Create form
const CreateMaterialImpact = (props) => {
  return (
    <Create {...props}>
      <SimpleForm>
        <ReferenceInput label="Account" source="accountId" reference="accounts">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput label="Material" source="materialId" reference="material">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput label="Project" source="projectId" reference="projects">
          <SelectInput optionText="name" />
        </ReferenceInput>

        {INDICATORS.map(ind =>
          STAGES.map(st => [
            <NumberInput key={matField(ind.key, st.suffix)}   source={matField(ind.key, st.suffix)}   label={`${ind.label} - ${st.label} (${ind.unit})`}    step="any" />,
            <NumberInput key={matFieldIS(ind.key, st.suffix)} source={matFieldIS(ind.key, st.suffix)} label={`${ind.label} - ${st.label} IS (${ind.unit})`}  step="any" />,
          ])
        )}
      </SimpleForm>
    </Create>
  );
};

// Edit form
const EditMaterialImpact = (props) => {
  return (
    <Edit {...props}>
      <SimpleForm>
        <ReferenceInput label="Account" source="accountId" reference="accounts">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput label="Material" source="materialId" reference="material">
          <SelectInput optionText="name" />
        </ReferenceInput>
        <ReferenceInput label="Project" source="projectId" reference="projects">
          <SelectInput optionText="name" />
        </ReferenceInput>

        {INDICATORS.map(ind =>
          STAGES.map(st => [
            <NumberInput key={matField(ind.key, st.suffix)}   source={matField(ind.key, st.suffix)}   label={`${ind.label} - ${st.label} (${ind.unit})`}    step="any" />,
            <NumberInput key={matFieldIS(ind.key, st.suffix)} source={matFieldIS(ind.key, st.suffix)} label={`${ind.label} - ${st.label} IS (${ind.unit})`}  step="any" />,
          ])
        )}
      </SimpleForm>
    </Edit>
  );
};

// Resource definition
const materialImpactResource = () => <Resource
  name="materialImpact"
  options={{ label: 'Material impacts', menuGroup: 'Materials' }}
  list={MaterialImpactList}
  create={CreateMaterialImpact}
  edit={EditMaterialImpact}
  show={ShowMaterialImpact} />;

export default materialImpactResource;
